import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, CalendarDays, Activity, Stethoscope, FileText, AlertTriangle, Gauge, BookOpen, ArrowRight, Check, ShieldCheck, Clock, MapPin } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";

const fade = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

const INCLUDED = [
  { icon: Heart, t: "Complete echocardiogram" },
  { icon: Stethoscope, t: "Professional interpretation by a board-certified cardiologist" },
  { icon: FileText, t: "Echocardiogram results sent to your designated primary care provider" },
  { icon: AlertTriangle, t: "Appropriate notification / escalation process for urgent findings" },
  { icon: Gauge, t: "Blood pressure measurement before the exam for added cardiovascular context" },
  { icon: BookOpen, t: "Know Your Numbers blood-pressure education" },
  { icon: Activity, t: "Stroke warning-sign education" },
];

const STEPS = [
  { t: "Reserve Your Screening", d: "Patients will be able to choose an available Know Your Heart clinic appointment through our approved scheduling system. Online scheduling is not yet active — see below." },
  { t: "Attend Your Appointment", d: "Blood pressure is obtained and a complete echocardiogram is performed in a comfortable, welcoming environment." },
  { t: "Cardiologist Interpretation", d: "The echocardiogram is professionally interpreted by a board-certified cardiologist." },
  { t: "Results & Follow-Up", d: "Results are sent to your designated primary care provider. Patients are contacted according to the established clinical process when findings require timely follow-up." },
];

const LOCATIONS = [
  {
    city: "Sugar Land",
    slug: "sugar-land",
    line1: "7616 Branford Place, Suite 220",
    line2: "Sugar Land, TX 77479",
    map: "https://www.google.com/maps?q=7616%20Branford%20Place%20Suite%20220%20Sugar%20Land%20TX%2077479&output=embed",
  },
  {
    city: "Cypress",
    slug: "cypress",
    line1: "21212 Northwest Fwy #205",
    line2: "Cypress, TX 77429",
    map: "https://www.google.com/maps?q=21212%20Northwest%20Fwy%20%23205%20Cypress%20TX%2077429&output=embed",
  },
];

export default function KnowYourHeart() {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const goContact = () => navigate("/?service=kyh");

  return (
    <>
      <Navbar />
      <main className="pt-24">
        {/* HERO */}
        <section className="relative overflow-hidden bg-gradient-to-br from-secondary via-secondary to-primary">
          <div className="relative max-w-5xl mx-auto px-5 lg:px-8 py-20 lg:py-28 text-center">
            <motion.div {...fade}>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-4 py-1.5 mb-6">
                <Heart size={15} className="text-blue-300" fill="currentColor" />
                <span className="text-xs font-bold tracking-wide text-white">Know Your Heart · Patient Screening Clinic</span>
              </span>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white font-medium leading-[1.1]">
                A Complete Echocardiogram.<br />One Transparent $300 Self-Pay Price.
              </h1>
              <p className="mt-6 text-white/80 leading-relaxed max-w-2xl mx-auto text-lg">
                Know Your Heart provides an accessible self-pay cardiac screening option for
                individuals seeking additional information about their heart health through
                scheduled screening clinics. The service includes professional cardiac imaging
                with interpretation by a board-certified cardiologist and communication of
                results to the patient's designated primary care provider.
              </p>
              <div className="mt-8 inline-flex items-baseline gap-2 rounded-2xl bg-white px-8 py-4">
                <span className="font-heading text-4xl font-semibold text-secondary">$300</span>
                <span className="text-xs tracking-[0.2em] uppercase font-bold text-primary">Self-Pay</span>
              </div>
              <p className="mt-5 text-blue-200 text-sm max-w-xl mx-auto">
                Transparent pricing. No surprise bill from Cardiac Precision Diagnostics for the screening service.
              </p>
            </motion.div>
          </div>
        </section>

        {/* WHAT'S INCLUDED */}
        <section className="bg-white">
          <div className="max-w-6xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
            <motion.div {...fade} className="text-center max-w-2xl mx-auto mb-12">
              <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary">What's Included</span>
              <h2 className="font-heading text-3xl lg:text-4xl text-secondary mt-4">Your $300 Know Your Heart screening includes</h2>
            </motion.div>
            <div className="grid sm:grid-cols-2 gap-5">
              {INCLUDED.map((it, i) => (
                <motion.div {...fade} transition={{ ...fade.transition, delay: i * 0.05 }} key={i} data-testid={`included-${i}`} className="flex items-start gap-4 rounded-2xl border border-border p-6 bg-muted/30">
                  <span className="h-11 w-11 shrink-0 rounded-xl bg-primary/10 flex items-center justify-center">
                    <it.icon size={20} className="text-primary" />
                  </span>
                  <p className="text-secondary/90 font-medium leading-relaxed pt-1.5">{it.t}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* WHY */}
        <section className="bg-muted">
          <div className="max-w-3xl mx-auto px-5 lg:px-8 py-20 lg:py-24 text-center">
            <motion.div {...fade}>
              <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary">Why Know Your Heart?</span>
              <h2 className="font-heading text-3xl lg:text-4xl text-secondary mt-4 mb-6">Making heart health more accessible</h2>
              <p className="text-muted-foreground leading-relaxed text-lg mb-4">
                High deductibles and out-of-pocket healthcare expenses can sometimes create
                barriers for individuals seeking health information and preventive services.
              </p>
              <p className="text-muted-foreground leading-relaxed text-lg">
                Cardiac Precision Diagnostics created Know Your Heart to provide a transparent
                self-pay cardiac screening option. Patients receive a complete echocardiogram
                with professional cardiologist interpretation, and results are shared with their
                designated primary care provider to support continuity of care.
              </p>
            </motion.div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="bg-white">
          <div className="max-w-5xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
            <motion.div {...fade} className="max-w-2xl mb-14">
              <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary">How Know Your Heart Works</span>
              <h2 className="font-heading text-3xl lg:text-4xl text-secondary mt-4 leading-tight">A simple, four-step process</h2>
            </motion.div>
            <div className="flex flex-col">
              {STEPS.map((s, i) => (
                <motion.div {...fade} transition={{ ...fade.transition, delay: i * 0.06 }} key={i} data-testid={`kyh-step-${i}`} className="flex gap-6">
                  <div className="flex flex-col items-center">
                    <div className="h-14 w-14 shrink-0 rounded-full bg-primary text-white flex items-center justify-center font-heading text-xl font-semibold">{i + 1}</div>
                    {i < STEPS.length - 1 && <div className="w-px flex-1 bg-border my-2" />}
                  </div>
                  <div className={i < STEPS.length - 1 ? "pb-10 pt-2" : "pt-2"}>
                    <h3 className="font-heading text-xl lg:text-2xl text-secondary mb-2">{s.t}</h3>
                    <p className="text-muted-foreground leading-relaxed max-w-2xl">{s.d}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Charm scheduling placeholder — NOT active */}
            <motion.div {...fade} data-testid="kyh-scheduling-placeholder" className="mt-10 rounded-2xl border-2 border-dashed border-primary/40 bg-primary/5 p-8 text-center">
              <CalendarDays className="mx-auto text-primary mb-3" size={28} />
              <p className="font-heading text-2xl text-secondary mb-2">Request Your Screening Appointment</p>
              <p className="text-muted-foreground max-w-xl mx-auto">
                Online self-scheduling is being finalized. In the meantime, you can request an
                appointment and our team will follow up to confirm availability at your preferred
                location, date, and time.
              </p>
              <button data-testid="kyh-notify-btn" onClick={goContact} className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-bold text-white hover:bg-secondary transition-colors duration-200">
                Request an Appointment <ArrowRight size={18} />
              </button>
            </motion.div>
          </div>
        </section>

        {/* SCREENING LOCATIONS */}
        <section className="bg-muted">
          <div className="max-w-6xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
            <motion.div {...fade} className="text-center max-w-2xl mx-auto mb-12">
              <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary">Pop-Up Clinics</span>
              <h2 className="font-heading text-3xl lg:text-4xl text-secondary mt-4">Know Your Heart Pop-Up Clinics</h2>
              <p className="text-secondary/90 font-medium mt-4">
                Convenient cardiac screening appointments offered at select Sugar Land and Cypress locations.
              </p>
              <p className="text-muted-foreground mt-4 leading-relaxed">
                Pop-up clinics are typically scheduled 1–2 times per month at each location. Dates and
                appointment availability vary. Select your preferred location, date, and time when
                requesting an appointment, and our team will confirm availability.
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {LOCATIONS.map((loc) => (
                <motion.div {...fade} key={loc.slug} data-testid={`kyh-location-${loc.slug}`} className="rounded-3xl border border-border bg-white overflow-hidden flex flex-col">
                  <div className="p-7">
                    <div className="flex items-center gap-2">
                      <MapPin size={18} className="text-primary" />
                      <h3 className="font-heading text-xl text-secondary">{loc.city} — MedFlex Spaces</h3>
                    </div>
                    <span className="block text-[11px] tracking-[0.18em] uppercase font-bold text-primary/70 mt-1 ml-7 mb-2">Know Your Heart Pop-Up Clinic Location</span>
                    <p className="text-sm text-secondary/80 leading-relaxed ml-7">{loc.line1}<br />{loc.line2}</p>
                  </div>
                  <div className="border-t border-border">
                    <iframe
                      title={`Know Your Heart screening location — ${loc.city}`}
                      src={loc.map}
                      className="w-full h-56 block"
                      style={{ border: 0 }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      allowFullScreen
                    />
                  </div>
                  <div className="p-6 mt-auto">
                    <button data-testid={`kyh-location-cta-${loc.slug}`} onClick={goContact} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary py-3 text-sm font-bold text-white hover:bg-secondary transition-colors duration-200">
                      Request an Appointment <ArrowRight size={16} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* KNOW YOUR NUMBERS */}
        <section className="bg-secondary text-white">
          <div className="max-w-3xl mx-auto px-5 lg:px-8 py-20 lg:py-24 text-center">
            <motion.div {...fade}>
              <span className="text-xs tracking-[0.25em] uppercase font-bold text-blue-300">Know Your Numbers</span>
              <h2 className="font-heading text-3xl lg:text-4xl mt-4 mb-6">Heart health extends beyond your echocardiogram</h2>
              <p className="text-white/75 leading-relaxed text-lg">
                Know Your Heart patients receive educational information about blood-pressure
                numbers and stroke warning signs to help them become better informed about
                cardiovascular health.
              </p>
            </motion.div>
          </div>
        </section>

        {/* MEDICAL DISCLAIMER */}
        <section className="bg-white">
          <div className="max-w-4xl mx-auto px-5 lg:px-8 py-16">
            <motion.div {...fade} data-testid="kyh-disclaimer" className="rounded-2xl border border-border bg-muted/40 p-7 lg:p-9">
              <div className="flex items-center gap-3 mb-4">
                <ShieldCheck className="text-primary" size={22} />
                <h3 className="font-heading text-xl text-secondary">Important Medical Disclaimer</h3>
              </div>
              <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
                <p>Know Your Heart is a cardiac screening service and is not a substitute for a comprehensive medical evaluation, diagnosis, treatment, or emergency medical care. Screening does not guarantee the absence of cardiovascular disease.</p>
                <p>Individuals experiencing chest pain, difficulty breathing, signs or symptoms of stroke, or another possible medical emergency should seek immediate emergency medical attention.</p>
                <p>Individuals with symptoms, known cardiovascular conditions, abnormal findings, or other medical concerns should follow up with an appropriate licensed healthcare provider.</p>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
